import {
    useEffect,
    useState,
    useCallback
} from "react";
import { ROUTE_SPECIFIC_COST_ESTIMATE_SUMMARY } from "../../routes/Routes";
import TabPanel, { Item as TabPanelItem } from 'devextreme-react/tab-panel';
import { BaseToggle } from "../../components/BaseToggle/BaseToggle";
import { KeyboardNavigation } from 'devextreme-react/data-grid';
import FiscalYearSelectDropdown from '../FiscalYearSelectDropdown';
import DataGridSpecificCostEstimateSummary from "./DataGridSpecificCostEstimateSummary";
import GetFetch from '../../hooks/GetFetch';
import '../SpecificCostEstimate/SpecificCostEstimate.scss';
import './SpecifiCostEstimateSummary.scss';

export default function SpecificCostEstimateSummary() {
    const {title} = ROUTE_SPECIFIC_COST_ESTIMATE_SUMMARY;
    const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
    const [enableToggle, setEnableToggle] = useState(true)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [summaryCostsTabData, setSummaryCostsTabData] = useState({
        0: null, // Current Year == Fiscal Year
        1: null, // Budget Year == Fiscal Year +1 
        2: null, // Budget Year+1 == Fiscal Year +2
        3: null, // Budget Year+2 == Fiscal Year +3
        4: null  // Budget Year+3 == Fiscal Year +4
    });
    const [isTabDataReset, setIsDataReset] = useState(false);
    const [groupingToggle, setGroupingToggle] = useState(true);
    const [showDataGrid, setShowDataGrid] = useState(false);
    

    const fetchSummaryCostsByFiscalYear = useCallback(async (tabIndex, year) => {
        setLoading(true);
        let fiscalYear;
        
        if (year) { 
            fiscalYear = year 
        } else { 
            fiscalYear = selectedFiscalYear + tabIndex 
        }

        const { Success, Message, Errors } = await GetFetch(`/v1/SummaryCostEstimate/${fiscalYear}/1,2,3,4,5,6,7,8,9,10,11`);  //passing in all regions;
        
        if (Success) {
            setEnableToggle(false);
            setSummaryCostsTabData(prevData => ({
                ...prevData,
                [tabIndex]: Message
            }));
        };
        
        if (Errors.length > 0) {
            setError(true);
            setEnableToggle(true);
            if (Errors[0].Message === "No records found at the moment") {
                console.log(Errors[0].Message);
            };
        };

        setShowDataGrid(true);
        setLoading(false);
    }, [selectedFiscalYear, summaryCostsTabData]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setSelectedTabIndex(0);
            setSummaryCostsTabData({ 0: null, 1: null, 2: null, 3: null, 4: null });
            setIsDataReset(true);
        };
    };

    const handleSubmit = async (param) => {
        setSelectedTabIndex(0);
        setSummaryCostsTabData({ 0: null, 1: null, 2: null, 3: null, 4: null });
        setSelectedFiscalYear(param);
        fetchSummaryCostsByFiscalYear(0, param);
    };

    useEffect(()=> {
        if (isTabDataReset) {
            fetchSummaryCostsByFiscalYear(0);
            setIsDataReset(false);
        };
    }, [isTabDataReset]);

    const onTabSelectionChanged = (e) => {
        const newTabIndex = e.component.option('selectedIndex');
        setSelectedTabIndex(newTabIndex);
        fetchSummaryCostsByFiscalYear(newTabIndex);
    };

    const hint = groupingToggle ?
        "Click to enable grouping" : "Click to disable grouping";

    const switchElemAttr = { id: 'switch-element-attribute' };


    return (
        <div>
            <h1>{ title }</h1>
            <em>
                Please select a current year
            </em>
            <div className="inline__div--wrapper">
                <FiscalYearSelectDropdown
                    inputAttribute={{ "aria-label": "Current Year *" }}
                    enableSearch={true}
                    label={"Current Year"}
                    onEnterKeyDown={handleKeyDown}
                    value={selectedFiscalYear}
                    optionUpdatedByUser={({ selectedItem }) => handleSubmit(selectedItem)}
                    isVisible={true}
                />
                <BaseToggle
                    disabled={enableToggle}
                    elementAttribute={switchElemAttr}
                    hint={hint}
                    name={"toggle grouping"}
                    valueUpdatedByUser={({value}) => setGroupingToggle(value)}
                    rightToLeftEnabled={true}
                    switchedOnText={"Enable Grouping"}
                    switchedOffText={"Disable Grouping"}
                    width={120}
                    value={groupingToggle} 
                />
            </div>
            <div className='inline-div--tab-panel-wrapper'>
                <TabPanel
                    disabled={selectedFiscalYear === '' ? true : false}
                    id="tabPanel" 
                    onSelectionChanged={onTabSelectionChanged} 
                    selectedIndex={selectedTabIndex}
                >
                    <KeyboardNavigation enabled={true} />
                    <TabPanelItem 
                        aria-label="cy-cost-est"
                        id="cy-cost-est" 
                        title={selectedFiscalYear === '' ? 'Current Year' : `Current Year (${selectedFiscalYear})`} 
                    >
                        <DataGridSpecificCostEstimateSummary 
                            error={error}
                            fiscalYear={selectedFiscalYear} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            showDataGrid={showDataGrid}
                            summaryCosts={summaryCostsTabData[0]} 
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est"
                        id="budget-year-cost-est" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 1' :  `Budget Year (${selectedFiscalYear + 1})`} 
                    >
                        <DataGridSpecificCostEstimateSummary 
                            error={error}
                            fiscalYear={selectedFiscalYear + 1} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            showDataGrid={showDataGrid}
                            summaryCosts={summaryCostsTabData[1]} 
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+1"
                        id="budget-year-cost-est+1" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 2' :  `Budget Year (${selectedFiscalYear + 2})`} 
                    >
                        <DataGridSpecificCostEstimateSummary 
                            error={error}
                            fiscalYear={selectedFiscalYear + 2} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            showDataGrid={showDataGrid}
                            summaryCosts={summaryCostsTabData[2]} 
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+2"
                        id="budget-year-cost-est+2" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 3' :  `Budget Year (${selectedFiscalYear + 3})`} 
                    >
                        <DataGridSpecificCostEstimateSummary 
                            error={error}
                            fiscalYear={selectedFiscalYear + 3} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            showDataGrid={showDataGrid}
                            summaryCosts={summaryCostsTabData[3]} 
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+4"
                        id="budget-year-cost-est+4" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 4' :  `Budget Year (${selectedFiscalYear + 4})`} 
                    >
                        <DataGridSpecificCostEstimateSummary 
                            error={error}
                            fiscalYear={selectedFiscalYear + 4} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            showDataGrid={showDataGrid}
                            summaryCosts={summaryCostsTabData[4]} 
                        />
                    </TabPanelItem>
                </TabPanel>
            </div>
        </div>
    );
}